import React, { useState, useRef } from "react";
import { CloseOutlined } from "@ant-design/icons";
import "./BrochureModal.scss";

function BrochureModal({ showBrochureModal, setShowBrochureModal, brochurePdfUrlRef, scriptUrl }) {
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  // Google Apps Script URL - replace with your actual web app URL
  const SCRIPT_URL = scriptUrl || 'https://script.google.com/macros/s/AKfycbzX5sIBWOwkcZY9wqpzBGLsAHdzZGQRaxTz6dFIjPYv5QvYjK99Ogx7ZMiprasUmVkFvw/exec';

  const handleClose = () => {
    setIsClosing(true);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, "", "/");
    }
    setTimeout(() => {
      setShowBrochureModal(false);
      setIsClosing(false);
      // Reset form when closing
      setFormData({ name: "", phone: "", email: "" });
      setSubmitStatus("");
    }, 300);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    // Create form data with timestamp
    const formDataWithTimestamp = {
      ...formData,
      timestamp: new Date().toISOString()
    };

    try {
      // First try: JSON with proper CORS handling
      const res = await fetch(SCRIPT_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formDataWithTimestamp),
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'omit' // Don't send cookies
      });
      
      if (res.ok) {
        const result = await res.json();
        console.log('Form submission result:', result);
        setSubmitStatus("success");
        setFormData({ name: "", phone: "", email: "" });
        // Close modal after successful submission
        if (brochurePdfUrlRef && brochurePdfUrlRef.current) {
          brochurePdfUrlRef.current.click();
        }
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        console.error('Response not ok:', res.status, res.statusText);
        setSubmitStatus("error");
      }
    } catch (err) {
      console.log('JSON submission failed, trying FormData fallback:', err);
      
      // Fallback 1: FormData with CORS
      try {
        const formDataObj = new FormData();
        Object.entries(formDataWithTimestamp).forEach(([key, value]) => {
          formDataObj.append(key, value);
        });
        
        const fallbackRes = await fetch(SCRIPT_URL, { 
          method: 'POST', 
          body: formDataObj,
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (fallbackRes.ok) {
          const fallbackResult = await fallbackRes.json();
          console.log('Fallback submission result:', fallbackResult);
          setSubmitStatus("success");
          setFormData({ name: "", phone: "", email: "" });
          if (brochurePdfUrlRef && brochurePdfUrlRef.current) {
            brochurePdfUrlRef.current.click();
          }
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          console.error('Fallback response not ok:', fallbackRes.status, fallbackRes.statusText);
          setSubmitStatus("error");
        }
      } catch (fallbackError) {
        console.log('FormData fallback failed, trying no-cors mode:', fallbackError);
        
        // Fallback 2: no-cors mode (can't read response but submission might work)
        try {
          const noCorsRes = await fetch(SCRIPT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formDataWithTimestamp),
            mode: 'no-cors'
          });
          
          // With no-cors, we can't read the response, so assume success
          console.log('No-cors submission sent (cannot verify response)');
          setSubmitStatus("success");
          setFormData({ name: "", phone: "", email: "" });
          // Only click if ref exists
          if (brochurePdfUrlRef && brochurePdfUrlRef.current) {
            brochurePdfUrlRef.current.click();
          }
          setTimeout(() => {
            handleClose();
          }, 2000);
        } catch (noCorsError) {
          console.error('All submission methods failed:', noCorsError);
          setSubmitStatus("error");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      if (showBrochureModal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
      return () => {
        document.body.style.overflow = "unset";
      };
    }
  }, [showBrochureModal]);

  if (!showBrochureModal) return null;

  return (
    <div className="brochure-modal-overlay" onClick={handleClose}>
      <div
        className={`brochure-modal-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="brochure-modal-close" onClick={handleClose}>
          <CloseOutlined />
        </button>
        <div className="brochure-modal-body">
          <div className="form-container">
            {/* <h2>Contact Information</h2> */}
            <p>Please fill in the details to receive more information about our project.</p>
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email address"
                />
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>

              {submitStatus === "success" && (
                <div className="success-message">
                  Thank you! Your information has been submitted successfully.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="error-message">
                  There was an error submitting your information. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrochureModal;
